import { SUPPORTED_IMAGE_MIME_TYPES } from '../../constants/uploads';
import type { GlbAnalysisIssue, ParsedGlbBinary, ParsedRuntimeGlb } from './types';

export function analyzeTextures(binary: ParsedGlbBinary, runtime: ParsedRuntimeGlb): GlbAnalysisIssue[] {
  const issues: GlbAnalysisIssue[] = [];

  const imageMap = new Map(binary.images.map((image) => [image.index, image]));
  const textureMap = new Map(binary.textures.map((texture) => [texture.index, texture]));

  for (const image of binary.images) {
    if (image.uri && /^(https?:)?\/\//i.test(image.uri)) {
      issues.push({
        code: 'texture.external-uri',
        severity: 'error',
        message: `Image "${image.name ?? image.index}" depends on an external URI, which is not allowed.`,
        path: `images[${image.index}]`,
      });
    }

    if (image.mimeType && !SUPPORTED_IMAGE_MIME_TYPES.has(image.mimeType)) {
      issues.push({
        code: 'texture.mime.unsupported',
        severity: 'error',
        message: `Unsupported texture MIME type: ${image.mimeType}.`,
        path: `images[${image.index}]`,
      });
    }
  }

  const slots = [
    'baseColorTextureIndex',
    'metallicRoughnessTextureIndex',
    'normalTextureIndex',
    'occlusionTextureIndex',
    'emissiveTextureIndex',
  ] as const;

  for (const material of binary.materials) {
    for (const slot of slots) {
      const textureIndex = material[slot];
      if (textureIndex == null) continue;

      const texture = textureMap.get(textureIndex);
      if (!texture) {
        issues.push({
          code: 'texture.slot.missing-texture',
          severity: 'error',
          message: `Material "${material.name ?? material.index}" references missing texture ${textureIndex} for ${slot}.`,
          path: `materials[${material.index}]`,
        });
        continue;
      }

      if (texture.source == null) {
        issues.push({
          code: 'texture.slot.missing-source',
          severity: 'error',
          message: `Texture ${texture.index} used by "${material.name ?? material.index}" has no image source.`,
          path: `textures[${texture.index}]`,
        });
        continue;
      }

      if (!imageMap.has(texture.source)) {
        issues.push({
          code: 'texture.slot.missing-image',
          severity: 'error',
          message: `Texture ${texture.index} used by "${material.name ?? material.index}" points to missing image ${texture.source}.`,
          path: `textures[${texture.index}]`,
        });
      }
    }
  }

  void runtime;

  return issues;
}
